import React, { useState, useEffect } from 'react';
import JqueryLoad from '../../../JqueryLoad/JqueryLoad';
import { RefundTransactionsListing } from '../../../api/apiHandler';
import Select from 'react-select';
import DataTable from 'react-data-table-component';
import { CButton, CFormInput, CInputGroup } from '@coreui/react';
import FontAwesome from 'react-fontawesome';
import { useNavigate } from 'react-router-dom';
import customStyles from '../../../utils/dataTableStyles';
import Hdashboard from '../../../components/Hdashboard';
import useDebounce from '../../../utils/useDebounce';
import { formatMoney } from '../../../utils/formatMoney';

// Multi-Day per-meeting partial refunds (stripe_model.refund_selected_meetings)
// don't get their own transaction table on the backend — every meeting refunded
// in one Stripe call shares the same tbl_enrolled_meeting.refund_id, so the API
// groups by that column and returns one row per refund batch here.
export default function MeetingRefundList({ isLoader }) {

    const [refundData, setRefundData] = useState([]);
    const navigate = useNavigate();
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [search, setSearch] = useState('');
    const debounceSearch = useDebounce(search, 750);

    useEffect(() => {
        fetchRefundTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPeriod, debounceSearch]);

    const fetchRefundTransactions = () => {
        isLoader(true);
        RefundTransactionsListing({ period: selectedPeriod, search })
            .then((response) => {
                if (response.code === "1") {
                    const dataWithRowCount = response.data.map((row, index) => ({ ...row, count: index + 1 }));
                    setRefundData(dataWithRowCount);
                } else {
                    setRefundData([]);
                }
                isLoader(false);
            })
            .catch(() => {
                setRefundData([]);
                isLoader(false);
            });
    };

    const handlePeriodChange = (selectedOption) => {
        setSelectedOption(selectedOption);
        setSelectedPeriod(selectedOption.value);
    };

    // ---- "Class Date" / "Total Meetings" display rules ---------------------
    //
    // classDateText: a Multi-Day purchase is ONE charge covering a SPAN of
    // meetings, so it prints "first - last". Every other shape has a single
    // meeting and the API returns class_start_date === class_end_date, so it
    // prints that one date. Keyed on the DATES rather than the class type so it
    // works whatever vocabulary the leg labels class_type with.
    //
    // totalMeetingsText: how many meetings the row is for. 0 / null means the row
    // has no meetings behind it (quiz, offline course), which reads as "-".
    const classDateText = (row) => {
        const start = row?.class_start_date;
        const end = row?.class_end_date;
        if (start && end && start !== end) return `${start} - ${end}`;
        return row?.class_date ? row.class_date : (start || '-');
    };

    const totalMeetingsText = (row) => {
        const total = Number(row?.total_meetings);
        return Number.isFinite(total) && total > 0 ? String(total) : '-';
    };

    const refundColumns = [
        {
            name: 'No',
            selector: row => row.count,
            sortable: false,
            width: "70px"
        },
        {
            name: 'Refund Date',
            selector: row => row.refund_date,
            sortable: true,
            width: "160px"
        },
        // NEW: "Class Date" — the date of the class / meeting this row is about,
        // as distinct from the date beside it, which is when the money moved.
        // Supplied by the API as `class_date` (dd/mm/YYYY). A row here is a refund BATCH that can cover several
        // meetings (see the Meetings column), so the date is the earliest meeting
        // the batch refunded.
        {
            name: 'Class Date',
            // A Multi-Day row prints its span ("first - last") rather than one
            // date; see classDateText. Old single-date selector:
            // selector: row => row.class_date ? row.class_date : '-',
            // cellExport: row => row.class_date ? row.class_date : '-',
            selector: row => classDateText(row),
            cellExport: row => classDateText(row),
            sortable: true,
            // A Multi-Day row prints a RANGE here ("17/08/2026 - 25/08/2026"),
            // which react-data-table-component clips with an ellipsis when it does
            // not fit. wrap:true breaks it onto a second line instead of cutting it
            // off, and the column is widened so it usually fits on one.
            wrap: true,
            // width: "160px"
            width: "190px"
        },
        // "Total Meetings" — the number of meetings this refund batch covers.
        // This is the ONLY column that states that number: the old 'Meetings'
        // column further right printed the same `meeting_count` and has been
        // removed so the list does not say it twice. See totalMeetingsText.
        {
            name: 'Total Meetings',
            selector: row => totalMeetingsText(row),
            cellExport: row => totalMeetingsText(row),
            sortable: true,
            width: "160px"
        },
        {
            name: 'Initiated By',
            selector: row => row.initiated_by,
            sortable: true,
            width: "130px"
        },
        {
            name: 'Teacher',
            selector: row => row.teacher_name,
            sortable: true,
            width: "170px"
        },
        {
            name: 'Learner',
            selector: row => row.learner_name,
            sortable: true,
            width: "170px"
        },
        {
            name: 'Class',
            selector: row => row.activity_title,
            sortable: true,
            width: "180px"
        },
        // NOTE: the old 'Meetings' column stood here; 'Total Meetings' above
        // already prints this row's meeting_count.
        {
            name: 'Base Amount',
            selector: row => formatMoney(row.base_amount, '$', ''),
            sortable: true,
            width: "130px"
        },
        {
            name: 'Tax',
            selector: row => formatMoney(row.tax_amount, '$', ''),
            sortable: true,
            width: "110px"
        },
        {
            name: (
                <>
                    Coupon<br />Adjustment
                </>
            ),
            selector: row => formatMoney(row.coupon_adjustment, '$', ''),
            sortable: true,
            width: "130px"
        },
        {
            name: 'Total Refund',
            selector: row => formatMoney(row.refund_amount, '$', ''),
            sortable: true,
            width: "130px"
        },
        {
            name: 'View Details',
            cell: (row) => (
                <div className="d-flex justify-content-center">
                    <div className="col me-2">
                        <FontAwesome
                            name="view"
                            className="fs-5 fa-solid fa-eye mx-2 my-2 bg-light text-secondary"
                            onClick={() => navigate("/meeting_refund_details", { state: row })}
                        ></FontAwesome>
                    </div>
                </div>
            ),
        }
    ];

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <>
            <JqueryLoad />
            <Hdashboard />
            <main>
                <div className="dashboard-app d-lg-flex px-5 mt-1">
                    <header className="dashboard-toolbar d-lg-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <a className="menu-toggle align-items-center d-lg-none d-flex">
                                <i className="fa fa-bars" />
                            </a>
                            <a href="index.php" className="d-lg-none d-block brand-logo">
                                <img src="./assets/images/logo.png" alt="logo" />
                            </a>
                        </div>
                    </header>

                    <span onClick={handleBack} className="mt-3">
                        <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.86172 2.225L8.37839 0.75L0.136719 9L8.38672 17.25L9.86172 15.775L3.08672 9L9.86172 2.225Z" fill="#131313" ></path>
                        </svg>
                        <span className="ms-2">Back</span>
                    </span>

                    <div className="d-flex justify-content-between title mt-3 mb-2">
                        <p className="fs-3 fw-500">Multi-Day Refunds History</p>
                    </div>

                    <div className='row mt-3'>
                        <div className='col-12 col-sm-6 col-md-4 col-xxl-2'>
                            <label className="mb-2">Select Time Period :</label>
                            <Select
                                options={[
                                    { value: "daily", label: "Daily" },
                                    { value: "weekly", label: "Weekly" },
                                    { value: "monthly", label: "Monthly" },
                                    { value: "yearly", label: "Yearly" },
                                ]}
                                className="skip_pdf"
                                id="period-select"
                                value={selectedOption}
                                onChange={handlePeriodChange}
                                styles={{
                                    width: "100%",
                                    maxWidth: "200px",
                                    minHeight: "40px"
                                }}
                                placeholder="Select Time Period"
                            />
                        </div>
                    </div>

                    <div className="mt-4 mb-4">
                        <CInputGroup>
                            <CFormInput type="text" placeholder="Search" value={search} onChange={(e) => {
                                setSearch(e.target.value);
                            }} />
                            <CButton style={{ backgroundColor: "rgb(87, 8, 97)", color: "light" }}>
                                <FontAwesome className="fa-solid fa-search" name="search" />
                            </CButton>
                        </CInputGroup>
                    </div>

                    <div>
                        <div className="px-1 mt-5">
                            {refundData && refundData.length > 0 ? (
                                <DataTable columns={refundColumns} data={refundData} pagination customStyles={customStyles} />
                            ) : (
                                <p className="mt-5 text-center text-purple fw-500 fs-3 mt-5">No data Found</p>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
